import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import { createApiGateway } from '../apiGateway';
import {
  type FinancesFilters,
  type V2StatisticsResponse,
  type V2StatisticsParams,
  type FinancesTableRow,
} from '../types/finances';
import { toNumber } from '../helpers/number';

const apiGateway = createApiGateway();

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const NON_PAGINATED_GROUP_BY = new Set(['sspId', 'dspId']);

type SortDirection = 'ASC' | 'DESC';

interface OrderState {
  by?: string;
  direction?: SortDirection;
}

const shouldUsePagination = (filters: FinancesFilters): boolean => {
  const groupBy = filters.groupBy ?? [];
  if (groupBy.length === 0) {
    return false;
  }

  return groupBy.some((field) => !NON_PAGINATED_GROUP_BY.has(field));
};

const getDefaultOrder = (filters: FinancesFilters): OrderState => {
  const isTimeSeries = filters.format === 'by_day' || filters.format === 'by_hour';

  return isTimeSeries
    ? {
        by: 'datetime',
        direction: 'ASC',
      }
    : {
        by: 'profit',
        direction: 'DESC',
      };
};

const initialFilters: FinancesFilters = {
  dateRange: [dayjs().format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
  format: 'summary',
  groupBy: null,
  sspId: null,
  dspId: null,
  companyId: null,
  extraCols: null,
};

type PaginationState = {
  current: number;
  pageSize: number;
};

type FetchOptions = {
  page?: number;
  pageSize?: number;
  resetPagination?: boolean;
  order?: OrderState;
};

interface ChangeSortOptions {
  columnKey?: string;
  direction?: SortDirection;
  page?: number;
  pageSize?: number;
}

export const useFinancesData = () => {
  const [pendingFilters, setPendingFilters] = useState<FinancesFilters>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<FinancesFilters>(initialFilters);
  const [data, setData] = useState<V2StatisticsResponse | null>(null);
  const [warning, setWarning] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [shouldPaginate, setShouldPaginate] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [lastOffset, setLastOffset] = useState<number>(0);
  const [order, setOrder] = useState<OrderState>(() => getDefaultOrder(initialFilters));
  const paginationRef = useRef<PaginationState>(pagination);
  const orderRef = useRef<OrderState>(getDefaultOrder(initialFilters));

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  useEffect(() => {
    orderRef.current = order;
  }, [order]);

  const fetchData = useCallback(async (filtersToApply: FinancesFilters, options: FetchOptions = {}) => {
    try {
      setLoading(true);
      setError(null);

      const isTimeSeries = filtersToApply.format === 'by_day' || filtersToApply.format === 'by_hour';

      const params: V2StatisticsParams = {
        from: filtersToApply.dateRange[0],
        to: dayjs(filtersToApply.dateRange[1]).add(1, 'day').format('YYYY-MM-DD'),
        format: filtersToApply.format !== 'summary' ? filtersToApply.format : undefined,
        orderBy: isTimeSeries ? 'datetime' : 'profit',
        orderDirection: isTimeSeries ? 'ASC' : 'DESC',
      };

      if (filtersToApply.groupBy && filtersToApply.groupBy.length > 0) {
        params.groupBy = filtersToApply.groupBy.join(',');
      }

      if (filtersToApply.sspId && filtersToApply.sspId.length > 0) {
        params.sspId = filtersToApply.sspId.join(',');
      }

      if (filtersToApply.dspId && filtersToApply.dspId.length > 0) {
        params.dspId = filtersToApply.dspId.join(',');
      }

      if (filtersToApply.companyId && filtersToApply.companyId.length > 0) {
        params.companyId = filtersToApply.companyId.join(',');
      }

      if (filtersToApply.extraCols && filtersToApply.extraCols.length > 0) {
        params.extraCols = filtersToApply.extraCols.join(',');
      }

      const requiresPagination = shouldUsePagination(filtersToApply);
      const currentPagination = paginationRef.current;
      const nextPageSize = options.pageSize ?? (requiresPagination ? currentPagination.pageSize : DEFAULT_PAGE_SIZE);
      let nextPage = 1;
      if (requiresPagination) {
        if (options.page !== undefined) {
          nextPage = options.page;
        } else if (options.resetPagination) {
          nextPage = 1;
        } else {
          nextPage = currentPagination.current;
        }
      }
      const nextOffset = requiresPagination ? (nextPage - 1) * nextPageSize : 0;

      if (requiresPagination) {
        params.limit = nextPageSize;
        params.offset = nextOffset;
      }

      const defaultOrder = getDefaultOrder(filtersToApply);
      const baseOrder = options.order ?? orderRef.current;
      const nextOrder: OrderState = {
        by: baseOrder.by ?? defaultOrder.by,
        direction: baseOrder.by ? (baseOrder.direction ?? defaultOrder.direction) : undefined,
      };

      if (nextOrder.by) {
        params.orderBy = nextOrder.by;
      }
      if (nextOrder.direction) {
        params.orderDirection = nextOrder.direction;
      }

      const response = await apiGateway.statisticsV2.getV2Statistics(params);
      setData(response);
      setWarning(response.warning);
      setAppliedFilters(filtersToApply);

      setShouldPaginate(requiresPagination);
      setTotalCount(typeof response.count === 'number' ? response.count : (response.rows?.length ?? 0));
      setPagination(
        requiresPagination ? { current: nextPage, pageSize: nextPageSize } : { current: 1, pageSize: nextPageSize }
      );
      setLastOffset(nextOffset);
      orderRef.current = nextOrder;
      setOrder(nextOrder);
    } catch (err) {
      const responseBody = (err as { response?: { data?: unknown } }).response?.data;
      const message =
        typeof responseBody === 'string' && responseBody.trim().length > 0
          ? responseBody
          : ((err as Error).message ?? 'Failed to fetch data');
      setError(message);
      setData(null);
      setWarning(undefined);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load with default filters
  useEffect(() => {
    fetchData(initialFilters, { resetPagination: true });
  }, [fetchData]);

  const updatePendingFilters = useCallback((newFilters: Partial<FinancesFilters>) => {
    setPendingFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  }, []);

  const hasUnappliedChanges = useMemo(() => {
    return JSON.stringify(pendingFilters) !== JSON.stringify(appliedFilters);
  }, [pendingFilters, appliedFilters]);

  const applyFilters = useCallback(() => {
    const requiresPagination = shouldUsePagination(pendingFilters);
    const appliedRequiresPagination = shouldUsePagination(appliedFilters);
    const shouldResetPagination = requiresPagination !== appliedRequiresPagination || hasUnappliedChanges;
    const formatChanged = pendingFilters.format !== appliedFilters.format;
    const nextDefaultOrder = formatChanged ? getDefaultOrder(pendingFilters) : undefined;

    fetchData(pendingFilters, {
      resetPagination: shouldResetPagination || formatChanged,
      order: nextDefaultOrder,
    });
  }, [fetchData, pendingFilters, appliedFilters, hasUnappliedChanges]);

  const changePage = useCallback(
    (page: number, pageSize: number) => {
      fetchData(appliedFilters, { page, pageSize });
    },
    [fetchData, appliedFilters]
  );

  const changeSort = useCallback(
    ({ columnKey, direction, page, pageSize }: ChangeSortOptions) => {
      const nextOrder = columnKey && direction ? { by: columnKey, direction } : getDefaultOrder(appliedFilters);
      fetchData(appliedFilters, {
        order: nextOrder,
        page,
        pageSize,
        resetPagination: page === undefined,
      });
    },
    [fetchData, appliedFilters]
  );

  const transformToTableData = useCallback((): FinancesTableRow[] => {
    if (!data?.rows) return [];

    const baseIndex = lastOffset;

    return data.rows.map((row, index) => {
      const sspRequests = toNumber(row.sspRequests as number | string | undefined);
      const dspRequests = toNumber(row.dspRequests as number | string | undefined);
      const rawCoverage = toNumber(row.coverage as number | string | undefined);
      const rawWinRate = toNumber(row.winRate as number | string | undefined);

      // Normalize rates to fraction (0..1) for consistent sorting/formatting
      const coverage = rawCoverage > 1 ? rawCoverage / 100 : rawCoverage;
      const winRate = rawWinRate > 1 ? rawWinRate / 100 : rawWinRate;

      return {
        key: `${baseIndex + index + 1}`,
        index: baseIndex + index + 1,
        ...row,
        // Ensure numeric fields are numbers even if API returns formatted strings
        sspRequests,
        dspRequests,
        coverage,
        winRate,
        bids: toNumber(row.bids),
        impressions: toNumber(row.impressions),
        nurl: toNumber(row.nurl),
        burl: toNumber(row.burl),
        sspSpend: toNumber(row.sspSpend),
        dspSpend: toNumber(row.dspSpend),
        profit: toNumber(row.profit),
      } as FinancesTableRow;
    });
  }, [data, lastOffset]);

  return {
    pendingFilters,
    appliedFilters,
    updatePendingFilters,
    applyFilters,
    hasUnappliedChanges,
    data,
    warning,
    tableData: transformToTableData(),
    loading,
    error,
    shouldPaginate,
    pagination,
    totalCount,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    changePage,
    changeSort,
    order,
  };
};
